import { FC, useState } from 'react'
import {
  Modal,
  Typography,
  Box,
  Alert,
  TextField,
  Button,
  Paper,
  Autocomplete,
  IconButton,
  Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { AxiosError } from 'axios'
import { BranchData } from '@/utilities/types'

interface Area {
  id: number
  name: string
  code: string
  branches: any[]
  dateCreated: string
  dateModified: string
  deleted: boolean
}

interface UserModalProps {
  open: boolean
  onClose: () => void
  onAdd: (data: number[], areaName: string, areaCode: string) => void
  branchList: BranchData[]
  areas: Area[] // ✅ Area object list
  error: string
}

const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ModalContent = styled(Box)({
  width: 600,
  maxHeight: '80vh',
  backgroundColor: '#ffffff',
  borderRadius: 12,
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
})

const ModalTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 500,
  fontFamily: 'Gabarito',
  color: '#505470',
  marginLeft: 1,
})

export const AreaManagementForm: FC<UserModalProps> = ({
  open,
  onClose,
  onAdd,
  branchList,
  areas,
}) => {
  const [selectedBranches, setSelectedBranches] = useState<BranchData[]>([])
  const [areaName, setAreaName] = useState('')
  const [areaCode, setAreaCode] = useState('')
  const [error, setError] = useState('')

  const isDuplicateCode = areas.some(
    (area) => area.code.toLowerCase() === areaCode.trim().toLowerCase(),
  )

  const deduplicatedById = Array.from(
    new Map(branchList.map((branch) => [branch.id, branch])).values(),
  )

  const uniqueBranchesByName = Array.from(
    new Map(deduplicatedById.map((branch) => [branch.name, branch])).values(),
  )

  const filteredBranchOptions = uniqueBranchesByName.filter(
    (branch) => !selectedBranches.some((selected) => selected.id === branch.id),
  )

  const handleAdd = async () => {
    if (!selectedBranches.length || !areaName || !areaCode) {
      setError('Please complete all fields and select at least one branch.')
      return
    }

    if (isDuplicateCode) {
      setError('Area code already exists. Please enter a unique code.')
      return
    }

    const branchIDs = selectedBranches.map((item) => item.id)

    try {
      onAdd(branchIDs, areaName, areaCode)
      setSelectedBranches([])
      setAreaName('')
      setAreaCode('')
      setError('')
      onClose()
    } catch (err) {
      const axiosErr = err as AxiosError
      setError(
        axiosErr?.response?.data?.message ||
          'Something went wrong while creating the area.',
      )
    }
  }

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContent>
        <Box display="flex" flexDirection="column" justifyContent="flex-start">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding={4}
          >
            <ModalTitle>Add Area</ModalTitle>
            <IconButton
              onClick={onClose}
              style={{ color: '#000000' }}
              sx={{ width: 20, height: 20 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mx: 4, mb: 2, borderWidth: 1, color: '#B1B8BF' }} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Content */}
        <Box
          sx={{ overflowY: 'auto', px: 3, pt: 2, pb: 3, flex: 1, minHeight: 0 }}
        >
          <TextField
            label="Area Name"
            variant="outlined"
            value={areaName}
            onChange={(e) => setAreaName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Area Code"
            variant="outlined"
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            error={!!areaCode && isDuplicateCode}
            helperText={
              !!areaCode && isDuplicateCode
                ? 'Area code already exists.'
                : 'Enter a unique area code.'
            }
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Assigned Branches
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, minHeight: 80, maxHeight: 200, overflowY: 'auto' }}
            >
              {selectedBranches.length === 0 ? (
                <Typography color="text.secondary">
                  No branches assigned
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[...selectedBranches]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((branch, index) => (
                      <Paper
                        key={`${branch.id}-${index}`}
                        variant="outlined"
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography>
                            {branch.name}- {branch.dealerName}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              setSelectedBranches((prev) =>
                                prev.filter((p) => p.id !== branch.id),
                              )
                            }
                          >
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Autocomplete input */}
        <Box sx={{ px: 3, pb: 2, pt: 0, borderTop: '1px solid #e0e0e0' }}>
          <Autocomplete
            options={filteredBranchOptions}
            getOptionLabel={(option) =>
              `${option.name} - ${option.dealerName}` || ''
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, value) => {
              if (value && !selectedBranches.some((b) => b.id === value.id)) {
                setSelectedBranches((prev) => [...prev, value])
              }
            }}
            renderOption={(props, option) => (
              <li {...props}>
                {option.name} - {option.dealerName}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add Branch"
                variant="outlined"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                }}
              />
            )}
            PopperProps={{
              modifiers: [
                { name: 'flip', enabled: false },
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: { altAxis: true, tether: false },
                },
              ],
              placement: 'bottom-start',
            }}
          />
        </Box>

        {/* Footer actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          padding="16px 24px"
          borderTop="1px solid #e0e0e0"
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAdd}>
            Submit
          </Button>
        </Box>
      </ModalContent>
    </StyledModal>
  )
}
